import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  BlockStack,
  InlineStack,
  Button,
  Text,
  Box,
  EmptyState,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import { CustomModal } from "@shared/ui";
import {
  PlusIcon,
  EditIcon,
  DeleteIcon,
} from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { AgendaSwimlane } from "@features/events/types";
import AddEditTrackModal from "./AddEditTrackModal";
import { getLocalizedText } from "@shared/lib";

interface ManageTracksModalProps {
  isOpen: boolean;
  onClose: () => void;
  tracks: AgendaSwimlane[];
  onSave: (tracks: AgendaSwimlane[], originalTrackIndices: number[]) => void;
}

interface TrackItemProps {
  track: AgendaSwimlane;
  index: number;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
}

const TrackItem: React.FC<TrackItemProps> = ({
  track,
  index,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();

  return (
    <SortableResourceItem
      accessibilityLabel={t("eventDetails.agenda.tracks.editTrack")}
      dragHandleAccessibilityLabel={`Reorder ${getLocalizedText(track.title)}`}
    >
      <Box paddingBlock="300" paddingInline="200">
        <InlineStack align="space-between" blockAlign="center" gap="400">
          <InlineStack align="start" blockAlign="center" gap="300">
            <BlockStack gap="100">
              <Text as="span" variant="bodyMd" fontWeight="semibold">
                {getLocalizedText(track.title)}
              </Text>
              {track.description && (
                <Text as="span" variant="bodySm" tone="subdued">
                  {getLocalizedText(track.description)}
                </Text>
              )}
            </BlockStack>
          </InlineStack>

          <InlineStack gap="200">
            <Button
              icon={EditIcon}
              variant="plain"
              size="slim"
              onClick={() => onEdit(index)}
              accessibilityLabel={t("eventDetails.agenda.tracks.editTrack")}
            />
            <Button
              icon={DeleteIcon}
              variant="plain"
              size="slim"
              tone="critical"
              onClick={() => onDelete(index)}
              accessibilityLabel={t("eventDetails.agenda.tracks.deleteTrack")}
            />
          </InlineStack>
        </InlineStack>
      </Box>
    </SortableResourceItem>
  );
};

export default function ManageTracksModal({
  isOpen,
  onClose,
  tracks,
  onSave,
}: ManageTracksModalProps) {
  const { t } = useTranslation();
  const [localTracks, setLocalTracks] = useState<AgendaSwimlane[]>([]);
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [editingTrack, setEditingTrack] = useState<AgendaSwimlane | undefined>(
    undefined
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const originalTrackIndicesRef = useRef(new WeakMap<AgendaSwimlane, number>());
  const sortableIdsRef = useRef(new WeakMap<object, string>());
  const sortableIdCounterRef = useRef(0);

  useEffect(() => {
    if (isOpen) {
      const nextOriginalTrackIndices = new WeakMap<AgendaSwimlane, number>();
      tracks.forEach((track, index) => {
        nextOriginalTrackIndices.set(track, index);
      });

      originalTrackIndicesRef.current = nextOriginalTrackIndices;
      setLocalTracks([...tracks]);
      setIsAddEditModalOpen(tracks.length === 0);
      setEditingTrack(undefined);
      setIsEditMode(false);
    }
  }, [isOpen, tracks]);

  const getSortableTrackId = useCallback((track: AgendaSwimlane) => {
    console.debug("[ManageTracksModal::getSortableTrackId] resolve stable sortable id");

    const existingId = sortableIdsRef.current.get(track as object);

    if (existingId) {
      return existingId;
    }

    const nextId = `track-${sortableIdCounterRef.current++}`;
    sortableIdsRef.current.set(track as object, nextId);

    return nextId;
  }, []);

  const handleOpenAddModal = () => {
    setEditingTrack(undefined);
    setIsEditMode(false);
    setIsAddEditModalOpen(true);
  };

  const handleOpenEditModal = (index: number) => {
    setEditingTrack(localTracks[index]);
    setIsEditMode(true);
    setIsAddEditModalOpen(true);
  };

  const handleCloseAddEditModal = () => {
    setIsAddEditModalOpen(false);
    setEditingTrack(undefined);
    setIsEditMode(false);
  };

  const handleSaveTrack = (track: AgendaSwimlane) => {
    if (isEditMode && editingTrack) {
      const trackIndex = localTracks.findIndex((t) => t === editingTrack);
      if (trackIndex !== -1) {
        const originalTrackIndex = originalTrackIndicesRef.current.get(
          localTracks[trackIndex]
        );
        const newTracks = [...localTracks];
        newTracks[trackIndex] = track;
        originalTrackIndicesRef.current.set(
          track,
          typeof originalTrackIndex === "number" ? originalTrackIndex : -1
        );
        setLocalTracks(newTracks);
      }
      return;
    }

    originalTrackIndicesRef.current.set(track, -1);
    setLocalTracks([...localTracks, track]);
  };

  const handleDeleteTrack = (index: number) => {
    const newTracks = localTracks.filter((_, i) => i !== index);
    setLocalTracks(newTracks);
  };

  const handleSave = () => {
    const originalTrackIndices = localTracks.map(
      (track) => originalTrackIndicesRef.current.get(track) ?? -1
    );
    onSave(localTracks, originalTrackIndices);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const addEditTrackModalKey = `${isAddEditModalOpen}-${
    isEditMode
      ? localTracks.findIndex((track) => track === editingTrack)
      : "new"
  }`;

  return (
    <>
      <CustomModal
        open={isOpen}
        onClose={handleCancel}
        title={t("eventDetails.agenda.tracks.manageTitle")}
        size="large"
        primaryAction={{
          content: t("common.save"),
          onAction: handleSave,
        }}
        secondaryActions={[
          {
            content: t("common.cancel"),
            onAction: handleCancel,
          },
        ]}
      >
        <CustomModal.Section>
          <BlockStack gap="400">
            {localTracks.length > 0 ? (
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h3" variant="headingMd">
                    {t("eventDetails.agenda.tracks.currentTracks")} (
                    {localTracks.length})
                  </Text>
                  <Button
                    icon={PlusIcon}
                    variant="primary"
                    size="slim"
                    onClick={handleOpenAddModal}
                  >
                    {t("eventDetails.agenda.tracks.addTrack")}
                  </Button>
                </InlineStack>

                <Box
                  borderWidth="025"
                  borderColor="border"
                  borderRadius="200"
                  background="bg-surface"
                >
                  <SortableList
                    items={localTracks}
                    idForItem={(track) => getSortableTrackId(track)}
                    onChange={setLocalTracks}
                    showDividers
                    renderItem={(track, _context, index) => (
                      <TrackItem
                        track={track}
                        index={index}
                        onEdit={handleOpenEditModal}
                        onDelete={handleDeleteTrack}
                      />
                    )}
                  />
                </Box>
              </BlockStack>
            ) : (
              <EmptyState
                image=""
                action={{
                  content: t("eventDetails.agenda.tracks.addTrack"),
                  onAction: handleOpenAddModal,
                }}
              >
                <Text as="p">
                  {t("eventDetails.agenda.tracks.emptyStateDescription")}
                </Text>
              </EmptyState>
            )}
          </BlockStack>
        </CustomModal.Section>
      </CustomModal>

      <AddEditTrackModal
        key={addEditTrackModalKey}
        isOpen={isAddEditModalOpen}
        onClose={handleCloseAddEditModal}
        track={editingTrack}
        onSave={handleSaveTrack}
        isEditMode={isEditMode}
      />
    </>
  );
}
